import React, { useState, useCallback, useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import { useStore } from "../../../../../../hooks";
import { Icon, IconButton } from "../../../../../../elements";
import Alerts from "../../../../../../elements/v2/Alerts/Alerts";
import UserIndicators from "./UserIndicators";
import UserList from "./UserList";
import LinearProgress from "@mui/material/LinearProgress";
import { AdvancedSettingsAssignmentTypes } from "../../../../../../utils/enums";

import {
  SelectorWrapper,
  SelectorContentWrapper,
  SelectorDropdownWrapper,
  AvatarWrapper,
  LoadingProgressWrapper
} from "../UserSelector.styled";

const UserSelector = observer(
  ({ onChange, customerId, selectedUser = {} }) => {
    const [openDropdown, setOpenDropdown] = useState(false);
    const { marketingStore } = useStore();
    const { advancedSettingsStore } = marketingStore;
    const [selectedUserPhotoUrl, setSelectedUserPhotoUrl] = useState(null);
    const [userPhotos, setUserPhotos] = useState({});
    const [hasSelectedUser, setHasSelectedUser] = useState(!!selectedUser?.id);

    const users = useMemo(() => {
      return advancedSettingsStore?.usersItems || [];
    }, [advancedSettingsStore?.usersItems]);

    useEffect(() => {
      const loadPhotos = async () => {
        if (users.length > 0 && customerId) {
          try {
            const photos = await advancedSettingsStore.loadUserPhotos(users, customerId);
            setUserPhotos(photos);
          } catch (error) {
           advancedSettingsStore.isError(true);
          }
        }
      };

      loadPhotos();
    }, [users, customerId]);

    useEffect(() => {
      if (selectedUser?.id) {
        setSelectedUserPhotoUrl(userPhotos[selectedUser.id] || selectedUser.photo_url || null);
        setHasSelectedUser(true);
      } else {
        setSelectedUserPhotoUrl(null);
        setHasSelectedUser(false);
      }
    }, [selectedUser, userPhotos]);

    const mappedUsers = useMemo(() => {
      return users.map((u) => ({
        name: [u.first_name, u.last_name].filter(Boolean).join(' ') || 'Unknown User',
        value: u.id,
        role: u.role?.name || '',
        photo_url: userPhotos[u.id] || u.photo_url || undefined,
        display_photo_url: u.display_photo_url || undefined,
      }));
    }, [users, userPhotos]);

    const toggleDropdown = useCallback(() => {
      setOpenDropdown(prev => !prev);
    }, []);

    const onResetDropdown = useCallback(
      (id) => {
        setOpenDropdown(false);

        const selectedUser = mappedUsers.find(user => user.value === id);
        if (selectedUser) {
          advancedSettingsStore.setSelectedUser(id);
          setSelectedUserPhotoUrl(selectedUser.photo_url);
          setHasSelectedUser(true);
          onChange && onChange(id);
        } else {
          setHasSelectedUser(false);
        }
      },
      [onChange, advancedSettingsStore, mappedUsers],
    );

    const onCloseDropdown = useCallback(() => {
      setOpenDropdown(false);
    }, []);

    useEffect(() => {
      if (!openDropdown) return;

      const handleClickOutside = (event) => {
        if (!event.target.closest('.user-selector-wrapper')) {
          setOpenDropdown(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [openDropdown]);

    const showLinearProgress = advancedSettingsStore.isLoadingUsers &&
                              advancedSettingsStore.selectedAssignmentTypes[AdvancedSettingsAssignmentTypes.SINGLE];

    return (
      <>
        {advancedSettingsStore.isError && (
          <Alerts
            severity="error"
            title="Error"
            message={advancedSettingsStore.errorMessage}
            width="auAssign a to"
            top="61px"
            right="16px"
            onClose={() => advancedSettingsStore.setIsError(false)}
          />
        )}
        <SelectorWrapper className="user-selector-wrapper" aria-label="User Selector" role="combobox" aria-expanded={openDropdown} aria-haspopup="listbox" selectedUser={hasSelectedUser}>
          <AvatarWrapper>
            {!selectedUserPhotoUrl && <Icon name="carbonUserAvatarFilled" width="44px" height="44px" />}
            {selectedUserPhotoUrl && <img src={selectedUserPhotoUrl} alt="User Avatar" />}
          </AvatarWrapper>
          <SelectorContentWrapper>
              <UserIndicators
                userIsActive={selectedUser?.active}
                userName={selectedUser?.first_name && selectedUser?.last_name ? `${selectedUser.first_name} ${selectedUser.last_name}` : '------'}
                role={selectedUser?.role?.name || ''}
              />
          </SelectorContentWrapper>
          <SelectorDropdownWrapper openDropdown={openDropdown} selectedUser={hasSelectedUser}>
            <IconButton
              icon="expandMoreOutlined"
              width="24px"
              height="24px"
              onClick={toggleDropdown}
              aria-label={openDropdown ? "Close user list" : "Open user list"}
            />
          </SelectorDropdownWrapper>
          <UserList
            isVisible={openDropdown}
            items={mappedUsers}
            onSelect={onResetDropdown}
            onClose={onCloseDropdown}
            top={"63px"}
          />

          {showLinearProgress && (
            <LoadingProgressWrapper>
              <LinearProgress />
            </LoadingProgressWrapper>
          )}
        </SelectorWrapper>
      </>
    );
  },
);

UserSelector.displayName = 'UserSelector';

export default UserSelector;